/**
 * Unwrap the typed SNBT JSON the editor endpoint returns
 * (`{ $snbt: "double", value: 2.5 }`, int arrays, …).
 *
 * The viewer treats these as plain JS values. A future editor can keep
 * the wrappers on `raw` so a round-trip does not drop type suffixes.
 */

export interface SnbtTypedNumber {
  $snbt: 'byte' | 'short' | 'int' | 'long' | 'float' | 'double';
  value: number;
}

export interface SnbtTypedArray {
  $snbt: 'int_array' | 'long_array' | 'byte_array';
  value: number[];
}

export type SnbtWrapper = SnbtTypedNumber | SnbtTypedArray | { $snbt: string; value: unknown };

export function isSnbtWrapper(value: unknown): value is SnbtWrapper {
  return (
    !!value &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    '$snbt' in value &&
    'value' in (value as object)
  );
}

export function unwrapSnbt(value: unknown): unknown {
  if (isSnbtWrapper(value)) return value.value;
  return value;
}

export function unwrapNumber(value: unknown, fallback = 0): number {
  const v = unwrapSnbt(value);
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.length > 0) {
    const n = Number(v.replace(/[bBsSlLfFdD]$/, ''));
    if (Number.isFinite(n)) return n;
  }
  return fallback;
}

export function unwrapString(value: unknown): string | null {
  const v = unwrapSnbt(value);
  if (typeof v === 'string') return v;
  if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  return null;
}

export function unwrapBoolean(value: unknown, fallback = false): boolean {
  const v = unwrapSnbt(value);
  if (typeof v === 'boolean') return v;
  if (v === 1 || v === '1' || v === 'true') return true;
  if (v === 0 || v === '0' || v === 'false') return false;
  return fallback;
}

export function unwrapStringList(value: unknown): string[] {
  const v = unwrapSnbt(value);
  if (typeof v === 'string') return [v];
  if (!Array.isArray(v)) return [];
  const out: string[] = [];
  for (const entry of v) {
    const s = unwrapString(entry);
    if (s != null && s.length > 0) out.push(s);
  }
  return out;
}

export function asRecord(value: unknown): Record<string, unknown> | null {
  const v = unwrapSnbt(value);
  if (v && typeof v === 'object' && !Array.isArray(v)) return v as Record<string, unknown>;
  return null;
}

export function asArray(value: unknown): unknown[] {
  const v = unwrapSnbt(value);
  return Array.isArray(v) ? v : [];
}
